import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { Manifest } from '../api/authoring'
import { payloadId } from '../api/useIntent'
import { fileTarget } from '../model/records'
import { declaredReferences } from '../model/references'
import { ComponentName } from '../ui/ComponentName'
import { MediaPicker } from '../ui/MediaPicker'
import { ReferencePicker } from '../ui/ReferencePicker'

/** A reference as a payload states it: the target is a composite id. */
export interface EdgeValue {
  edgeId: string
  name: string
  id: string
  revision?: number
}

/**
 * The control for a payload member marked `x-edge`: a reference the
 * intent writes under one of the holder's edges.
 *
 * **Chosen, never typed.** The schema describes the member's four parts,
 * and a form drawing them would ask an author for an edge id, the edge's
 * name and a UUID. What the author is actually deciding is which
 * component, so that is the one thing this asks; the name comes off the
 * marker and the edge id is the one already held, or the name.
 *
 * What the edge accepts is the holder's declaration, read off the
 * manifest by the marker's value. A single accepted type that holds a
 * file gets the library picker, which can also take a new upload; any
 * other edge gets the general reference picker.
 */
export function EdgeField({
  label,
  edge,
  tenant,
  manifest,
  holder,
  value,
  onChange,
  disabled,
}: {
  label: string
  /** The edge's name, from `x-edge`. */
  edge: string
  tenant: string
  manifest: Manifest | undefined
  /** The type whose edge this is. */
  holder: string | undefined
  value: unknown
  onChange: (next: EdgeValue | undefined) => void
  disabled: boolean
}) {
  const [picking, setPicking] = useState(false)
  const declares = holder ? declaredReferences(manifest, holder).find((declared) => declared.name === edge) : undefined
  const held = isEdgeValue(value) ? value : undefined

  if (!declares) {
    return (
      <Alert severity="warning">
        {label} cannot be chosen here.
      </Alert>
    )
  }

  const choose = (id: string) => {
    setPicking(false)
    onChange({ edgeId: held?.edgeId || edge, name: edge, id: payloadId(tenant, id) })
  }
  const file = fileTarget(manifest, declares)

  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">{label}</Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {held ? (
          <ComponentName tenant={tenant} id={bare(held.id)} manifest={manifest} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nothing chosen.
          </Typography>
        )}
        <Button size="small" variant="outlined" disabled={disabled} onClick={() => setPicking(true)}>
          {held ? 'Choose another' : 'Choose'}
        </Button>
      </Stack>
      {file ? (
        <MediaPicker
          tenant={tenant}
          open={picking}
          typeName={file}
          manifest={manifest}
          chosen={held ? bare(held.id) : undefined}
          onClose={() => setPicking(false)}
          onChoose={(component) => choose(component.id)}
        />
      ) : (
        <ReferencePicker
          tenant={tenant}
          declares={picking ? declares : undefined}
          open={picking}
          onClose={() => setPicking(false)}
          onChoose={(target) => choose(target.id)}
          busy={disabled}
        />
      )}
    </Stack>
  )
}

function isEdgeValue(value: unknown): value is EdgeValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as EdgeValue).id === 'string' &&
    (value as EdgeValue).id !== ''
  )
}

/** A composite id's own value, which is what a read of the target takes. */
function bare(id: string): string {
  return id.slice(id.indexOf('#') + 1)
}
